import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于芒果视频',
    description: '芒果视频是蜜桃视频与糖豆视频之外值得推荐的优质视频平台，资源丰富、播放流畅，免费高清在线观看各类影视内容。',
    url: 'https://www.mangoshipin.com/about',
    isPartOf: {
      '@type': 'WebSite',
      name: '芒果视频',
      url: 'https://www.mangoshipin.com/',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '芒果视频',
    url: 'https://www.mangoshipin.com/',
    description: '优质视频平台，提供免费高清在线观影服务',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'service@mangoshipin.com',
      availableLanguage: ['Chinese'],
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 芒果视频 | 品牌介绍与联系方式"
        description="芒果视频品牌介绍：了解芒果视频的发展历程、品牌口碑、核心优势与联系方式。芒果视频作为蜜桃视频、糖豆视频之外的优质视频平台推荐。"
        keywords="芒果视频,芒果视频品牌,芒果视频介绍,视频平台,关于芒果视频,芒果视频联系方式"
        ogTitle="关于我们 - 芒果视频 | 品牌介绍与联系方式"
        ogDescription="了解芒果视频的发展历程、品牌口碑与核心优势。芒果视频是值得推荐的优质视频平台。"
        schema={{ ...aboutSchema, ...orgSchema }}
      />
      <Navbar />

      <main className="pt-20">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        <section className="py-10 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 font-heading mb-4">
              关于芒果视频
            </h1>
            <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              致力于为用户提供最优质的免费高清视频观影体验
            </p>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="lg:w-1/2">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20video%20streaming%20company%20office%20with%20warm%20orange%20accent%20walls%2C%20creative%20workspace%20with%20large%20screens%20displaying%20video%20content%2C%20collaborative%20environment%2C%20natural%20light%20from%20large%20windows%2C%20contemporary%20design&width=800&height=600&seq=mango-about-brand&orientation=landscape"
                  alt="芒果视频品牌"
                  className="w-full rounded-2xl"
                  title="芒果视频品牌 - 优质视频平台"
                />
              </div>
              <div className="lg:w-1/2">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
                  品牌故事
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                  芒果视频成立于2024年，是国内新兴的优质在线视频平台。我们的团队由一群热爱影视内容的年轻人组成，怀揣着"让每个人都能轻松享受高品质视频内容"的使命，致力于打造一个资源丰富、体验流畅、完全免费的在线观影平台。
                </p>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4">
                  在蜜桃视频、糖豆视频等传统平台之外，芒果视频以差异化定位迅速崛起。我们专注于综合视频资源的整合与优化，通过先进的技术架构和智能推荐算法，为用户提供个性化、高品质的观影体验。
                </p>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                  经过两年的发展，芒果视频已经成为国内最受欢迎的视频平台之一，拥有超过1000万活跃用户，日均播放量突破5000万次。我们将继续努力，不断提升服务质量，为用户带来更多优质内容。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-10 text-center">
              口碑与评价
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <div key={star} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-accent-500"></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "芒果视频真的很好用，资源比蜜桃视频和糖豆视频丰富多了，而且播放特别流畅，画质也很清晰。自从用了芒果视频，我已经卸载了其他视频App。"
                </p>
                <div className="text-xs text-foreground-500 font-medium">—— 追剧达人小李</div>
              </div>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <div key={star} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-accent-500"></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "对比了蘑菇视频和蓝莓视频，最终还是选择了芒果视频。免费高清这一点就赢了，而且界面设计很清爽，操作也很方便，强烈推荐！"
                </p>
                <div className="text-xs text-foreground-500 font-medium">—— 电影发烧友阿杰</div>
              </div>
              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <div key={star} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm text-accent-500"></i>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  "芒果视频的资源更新速度真的很快，最新电影和电视剧基本同步上线。画质清晰流畅，在家用电视投屏看大片，体验完全不输电影院。"
                </p>
                <div className="text-xs text-foreground-500 font-medium">—— 家庭用户王女士</div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-1/2">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-6">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们的客服团队将在24小时内回复您的消息。
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">电子邮箱</div>
                      <div className="text-sm font-medium text-foreground-800">service@mangoshipin.com</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                      <i className="ri-customer-service-2-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">客服热线</div>
                      <div className="text-sm font-medium text-foreground-800">400-888-6688</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">公司地址</div>
                      <div className="text-sm font-medium text-foreground-800">北京市朝阳区科技园南路18号芒果大厦</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/2">
                <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70">
                  <h3 className="text-lg font-semibold text-foreground-900 font-heading mb-5">
                    发送消息
                  </h3>
                  <form
                    id="about-contact-form"
                    data-readdy-form
                    className="space-y-4"
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.currentTarget;
                      const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value.trim();
                      if (honeypot) {
                        alert('消息已发送，我们会尽快回复您！');
                        form.reset();
                        return;
                      }
                      const formData = new FormData(form);
                      const submitAddr = 'https://readdy.ai/api/form/d9mvkqu8mbnljin59sb0';
                      try {
                        const res = await fetch(submitAddr, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                          body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                        });
                        const responseText = await res.text();
                        if (res.ok) {
                          alert('消息已发送，我们会尽快回复您！');
                          form.reset();
                        } else {
                          alert('发送失败，请稍后再试。');
                        }
                      } catch {
                        alert('网络错误，请稍后再试。');
                      }
                    }}
                  >
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的姓名 *
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱 *
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 *
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}